import {Component, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef, AfterViewInit} from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { MatTableDataSource } from '@angular/material/table';
import {EcommerceConnectionService} from "../../service-data/ecommerce-connection.service";
import {MediaMatcher} from "@angular/cdk/layout";
import {RoleChangeListener, StartupService} from "../../service-ui/startup.service";
import {Router} from "@angular/router";
import {LoggedInCallback} from "../../service-auth/cognito.service";
import {EcommerceConnection} from "../../../model/EcommerceConnection";
import {EcommerceOrder} from "../../../model/EcommerceOrder";
import {EcommerceOrderService} from "../../service-data/ecommerce-order.service";
import {OrganizationService} from "../../service-data/organization.service";
import {ProductService} from "../../service-data/product.service";
import {PrintNodeOrderService} from "../../service-data/print-node-order.service";
import {PrintNodeOrder} from "../../../model/PrintNodeOrder";
import {MatSnackBar} from "@angular/material/snack-bar";
import {Util} from "../../util/Util";
import {getTrackingUrl, splitTrackingNumbers} from "../../util/tracking-url";
import {MatDialog} from "@angular/material/dialog";
import {ConfirmDialogComponent} from "../confirm-dialog/confirm-dialog.component";
import {LanguagePipe} from "../../pipe/language.pipe";

interface WeekOption {
  value: Date;
  viewValue: string;
}

export type SortDirection = 'asc' | 'desc';

export interface SortCriterion {
  column: string;
  direction: SortDirection;
}

@Component({
  selector: 'app-ecommerce-orders',
  templateUrl: './ecommerce-orders.component.html',
  styleUrls: ['./ecommerce-orders.component.css']
})
export class EcommerceOrdersComponent implements OnInit, OnDestroy, LoggedInCallback, AfterViewInit, RoleChangeListener {
  @ViewChild('summaryElement') summaryElement: ElementRef;
  @ViewChild('filterButtonsElement') filterButtonsElement: ElementRef;
  mobileQuery: MediaQueryList;
  private _mobileQueryListener: () => void;

  protected readonly Util = Util;

  onDestroySubject: Subject<boolean> = new Subject();

  showSymbolMeanings: boolean = true;

  ecommerceConnections: EcommerceConnection[] = [];
  selectedEcommerceConnection: EcommerceConnection | null = null;
  selectedEcommerceConnectionId: string = 'ALL';

  ecommerceOrders: EcommerceOrder[] = [];
  selectedEcommerceOrder: EcommerceOrder | null = null;
  editingOrder: boolean = false;

  weekOptions: WeekOption[] = this.generateWeekOptions();
  selectedWeek: Date = this.getCurrentWeekStart();

  filteredOrders$: Observable<EcommerceOrder[]>;
  dataSource: MatTableDataSource<EcommerceOrder>;
  displayedColumns: string[] = ['ecommerceConnection', 'order', 'date', 'status', 'customer', 'items', 'revenueAndCost', 'tracking', 'details', 'actions'];

  /**
   * Multi-column sort state.
   *
   * The FIRST entry is the primary sort, the second the secondary, and so on.
   * Clicking a column header cycles that column asc -> desc -> off, and any
   * click promotes the column to the front of the stack, so the most recently
   * chosen column is always the primary sort and previously chosen ones become
   * the tie-breakers in the order they were chosen.
   */
  sortStack: SortCriterion[] = [{ column: 'date', direction: 'desc' }];

  /** Columns that carry no orderable value. */
  readonly unsortableColumns: string[] = ['details', 'actions'];

  /** Last set of orders coming out of the filter pipeline, kept so a sort change does not need a re-filter. */
  private lastFilteredOrders: EcommerceOrder[] = [];

  // Filter state
  selectedFilters: BehaviorSubject<string[]> = new BehaviorSubject(['All']);
  searchQuery: BehaviorSubject<string> = new BehaviorSubject('');
  selectedEcommerceConnectionSubject: BehaviorSubject<EcommerceConnection | undefined> = new BehaviorSubject(undefined);

  // Summary data
  ordersCount$: Observable<number>;
  orderItemsCount$: Observable<number>;
  ordersShippedCount$: Observable<number>;
  ordersHoldCount$: Observable<number>;
  ordersCostReviewCount$: Observable<number>;
  ordersNeedReviewCount$: Observable<number>;
  totalProductionCostShippedDelivered$: Observable<number>;

  private ngUnsubscribe = new Subject<void>();

  constructor(public router: Router, public startupService: StartupService, public productService: ProductService, public organizationService: OrganizationService, public changeDetectorRef: ChangeDetectorRef, public media: MediaMatcher, private ecommerceOrderService: EcommerceOrderService, private ecommerceConnectionService: EcommerceConnectionService, private printNodeOrderService: PrintNodeOrderService, private snackBar: MatSnackBar, private dialog: MatDialog) {
    this.startupService.addRoleChangeListener(this);
    this.mobileQuery = media.matchMedia('(max-width: 1024px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addListener(this._mobileQueryListener);

  }
  roleChanged(role: string, roleId: string): void {
    this.getData();
  }

  ngAfterViewInit(): void {
    this.getData();
  }

  isLoggedIn(message: string, isLoggedIn: boolean) {
    if (!isLoggedIn) {
      this.router.navigate(['/home/login']);
    } else {
      this.getData();
    }
  }

  getData(): void {
    this.selectedEcommerceConnection = undefined;
    this.selectedEcommerceConnectionSubject.next(undefined);
    this.ecommerceConnections = [];
    this.startupService.touchLastUserActivity();
    this.ecommerceConnectionService.getAddNotifier(true).pipe(takeUntil(this.onDestroySubject)).subscribe( o => {
      this.addUpdate(o);
    });
    this.ecommerceConnectionService.getUpdateNotifier().pipe(takeUntil(this.onDestroySubject)).subscribe( o => {
      this.addUpdate(o);
    });
    this.ecommerceConnectionService.getDeleteNotifier().pipe(takeUntil(this.onDestroySubject)).subscribe( o => {
    });

    this.ecommerceOrders = [];
    this.ecommerceOrderService.getAddNotifier(true).pipe(takeUntil(this.onDestroySubject)).subscribe( o => {
      this.addUpdateEcommerceOrder(o);
    });
    this.ecommerceOrderService.getUpdateNotifier().pipe(takeUntil(this.onDestroySubject)).subscribe( o => {
      this.addUpdateEcommerceOrder(o);
    });
    this.ecommerceOrderService.getDeleteNotifier().pipe(takeUntil(this.onDestroySubject)).subscribe( o => {
    });
    // this.ecommerceConnectionService.objects.forEach( o => {
    //   this.addUpdate(o);
    // });
  }
  contains(object: EcommerceConnection, array: EcommerceConnection[] ): boolean {
    for ( const o of array ) {
      if ( o.getId() === object.getId() ) {
        return true;
      }
    }
    return false;
  }

  addUpdate(o: EcommerceConnection) {
    try {
      if ( this.match(o) ) {
        if ( this.ecommerceConnections.findIndex(item => item.getId() === o.getId()) === -1) {
          this.ecommerceConnections.push(o);
        }
        // if ( !this.selectedEcommerceConnection && o ) {
        //   this.selectedEcommerceConnection = o;
        //   this.selectedEcommerceConnectionSubject.next(o);
        // }
      }
    } catch ( e ) {
      console.log(`ERROR in EcommerceOrdersComponent addUpdate ${o.getId()} ${e.message} ${this.startupService.selectedRole}:${this.startupService.selectedRoleId}`);
    }
    // if ( !this.selectedEcommerceConnection && this.ecommerceConnections.length > 0 ) {
    //   this.selectedEcommerceConnection = this.ecommerceConnections[0];
    //   this.editEcommerceConnection = new EcommerceConnection(this.selectedEcommerceConnection);
    // }
  }

  match(ecommerceConnection: EcommerceConnection ): boolean {
    if ( ecommerceConnection && ecommerceConnection.state === 'ACTIVE' ) {
      return true;
    }
    return false;
  }

  addUpdateEcommerceOrder(o: EcommerceOrder) {
    try {
      if ( this.matchEcommerceOrder(o) ) {
        if ( this.ecommerceOrders.findIndex(item => item.getId() === o.getId()) === -1) {
          this.ecommerceOrders.push(o);
        }
      }
    } catch ( e ) {
      console.log(`ERROR in EcommerceOrdersComponent addUpdate ${o.getId()} ${e.message} ${this.startupService.selectedRole}:${this.startupService.selectedRoleId}`);
    }
    // if ( !this.selectedEcommerceConnection && this.ecommerceConnections.length > 0 ) {
    //   this.selectedEcommerceConnection = this.ecommerceConnections[0];
    //   this.editEcommerceConnection = new EcommerceConnection(this.selectedEcommerceConnection);
    // }
  }

  matchEcommerceOrder(ecommerceOrder: EcommerceOrder ): boolean {
    if ( ecommerceOrder ) {
      return true;
    }
    return false;
  }

  ngOnInit(): void {
    this.startupService.isAuthenticated(this);
    this.getData();
    this.selectedFilters.next(['ALL']);
    this.filteredOrders$ = combineLatest([
      new BehaviorSubject(this.ecommerceOrderService.objects.filter(ecommerceOrder => {
        if (this.selectedEcommerceConnection ) {
          if ( ecommerceOrder.ecommerceconnection_ID === this.selectedEcommerceConnection.ID ) {
            return true;
          } else {
            return false;
          }
        }
        return true; // No filter by ecommerceConnection.ID if there is not a selected one
      })).asObservable(),
      // this.getEcommerceOrdersForEcommerceConnection(this.selectedEcommerceConnection),
      this.selectedFilters,
      this.searchQuery,
      this.selectedEcommerceConnectionSubject
      // ,
      // this.ecommerceOrderService.addNotifier,
      // this.ecommerceOrderService.updateNotifier,
      // this.ecommerceOrderService.deleteNotifier
    ]).pipe(
      // map(([orders, filters, query, addNotifier, updateNotifier, deleteNotifier]) => {
      map(([orders, filters, query, selectedEcommerceConnectionX]) => {
        // let filtered = orders;
        const ecommerceConnectionId = (this.selectedEcommerceConnection ? this.selectedEcommerceConnection.ID : 0);
        let filtered = orders; // orders.filter(ecommerceOrder => ecommerceOrder.ecommerceconnection_ID === ecommerceConnectionId );
        if ( ecommerceConnectionId !== 0 ) {
          filtered = filtered.filter( order => {
            if ( order.ecommerceconnection_ID === ecommerceConnectionId ) {
              return true;
            }
            return false;
          });
        }

        if (!filters.includes('ALL')) {
          filtered = filtered.filter(order => {
            let keep = false;
            if ( filters.includes('NEED_ATTENTION') && order.ignoreStatus === 'NOT_IGNORE' ) {
              if ( order.functionalStatus !== 'SUCCESS') {
                keep = true;
              }
              if ( order.releaseStatus === 'ERROR_REMOTE_ORDERID') {
                keep = true;
              }
            }
            if ( filters.includes('READY_TO_RELEASE') && order.ignoreStatus === 'NOT_IGNORE' ) {
              if ( order.releaseStatus === 'READY') {
                keep = true;
              }
            }
            if ( filters.includes('PROCESSING') && order.ignoreStatus === 'NOT_IGNORE' ) {
              if ( order.functionalStatus === 'SUCCESS' && order.releaseStatus === 'NOT_READY' ) {
                keep = true;
              }
            }
            if ( filters.includes('RELEASED') && order.ignoreStatus === 'NOT_IGNORE' ) {
              if ( order.releaseStatus === 'SUCCESS') {
                keep = true;
              }
            }
            if ( filters.includes('SHIPPED') && order.ignoreStatus === 'NOT_IGNORE' ) {
              if ( this.matchesShippedFilter(order) ) {
                keep = true;
              }
            }
            if ( filters.includes('ON_HOLD') && order.ignoreStatus === 'NOT_IGNORE' ) {
              if ( order.userHoldStatus === 'HOLD') {
                keep = true;
              }
            }
            if ( filters.includes('IGNORED') ) {
              if ( order.ignoreStatus === 'IGNORE') {
                keep = true;
              }
            }
            return keep;
          });
        } else { // Take the IGNORED ones out of All
          filtered = filtered.filter(order => {
            if ( order.ignoreStatus === 'IGNORE') {
                return false;
            }
            return true;
          });
        }

        if (query) {
          const lowerCaseQuery = query.toLowerCase();
          filtered = filtered.filter(order =>
            order.remoteOrderId.toLowerCase().includes(lowerCaseQuery) ||
            order.order_ID.toString().toLowerCase().includes(lowerCaseQuery) ||
            order.firstname.toLowerCase().includes(lowerCaseQuery) ||
            order.lastname.toLowerCase().includes(lowerCaseQuery) ||
            order.address1.toLowerCase().includes(lowerCaseQuery) ||
            order.city.toLowerCase().includes(lowerCaseQuery) ||
            order.state.toLowerCase().includes(lowerCaseQuery) ||
            order.postalcode.toLowerCase().includes(lowerCaseQuery) ||
            order.country.toLowerCase().includes(lowerCaseQuery) ||
            order.productionStatus.toLowerCase().includes(lowerCaseQuery) ||
            order.trackingNumbers.toLowerCase().includes(lowerCaseQuery) ||
            JSON.stringify(order.bookorder?.items)?.toLowerCase().includes(lowerCaseQuery) ||
            order.ecommerceConnection.type.toLowerCase().includes(lowerCaseQuery) ||
            // TODO: Status values in the filter ? order.status.toLowerCase().includes(lowerCaseQuery) ||
            order.shippingInfo.toLowerCase().includes(lowerCaseQuery)
          );
        }
        return filtered;
      })
    );

    this.filteredOrders$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(orders => {
      this.lastFilteredOrders = orders;
      this.dataSource = new MatTableDataSource(this.applySort(orders));
      this.updateSummaryData(orders);
    });
  }

  ngOnDestroy(): void {
    this.mobileQuery.removeListener(this._mobileQueryListener);
    this.onDestroySubject.next(true);
    this.onDestroySubject.unsubscribe();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  onEcommerceConnectionChange(ecommerceConnection: EcommerceConnection): void {
    this.selectedEcommerceConnection = ecommerceConnection;
    this.selectedEcommerceConnectionId = ecommerceConnection ? '' + ecommerceConnection.ID : 'ALL';
    this.selectedEcommerceConnectionSubject.next(this.selectedEcommerceConnection);
    this.updateDisplayedColumns();
  }

  onStoreSelectChange(value: string): void {
    this.selectedEcommerceConnectionId = value;
    if (value === 'ALL') {
      this.selectedEcommerceConnection = null;
      this.selectedEcommerceConnectionSubject.next(undefined);
    } else {
      const found = this.ecommerceConnections.find(ec => '' + ec.ID === value);
      this.selectedEcommerceConnection = found || null;
      this.selectedEcommerceConnectionSubject.next(found);
    }
    this.updateDisplayedColumns();
  }

  onWeekChange(event: MatSelectChange): void {
    this.selectedWeek = event.value;
    // Implement logic to filter orders by week if needed
  }

  onFilterChange(event: MatButtonToggleChange): void {
    const currentFilters = [...this.selectedFilters.value];
    const changedValue = event.value as string[];

    if ((!(currentFilters.includes('ALL')) && changedValue.includes('ALL') ) || changedValue.length === 0 ) {
      this.selectedFilters.next(['ALL']);
    } else {
      const allIndex = currentFilters.indexOf('ALL');
      if (allIndex > -1) {
        changedValue.splice(allIndex, 1);
      }
      this.selectedFilters.next(changedValue);
    }
  }

  toggleFilter(filter: string): void {
    const current = [...this.selectedFilters.value];
    if (filter === 'ALL') {
      this.selectedFilters.next(['ALL']);
    } else {
      // Remove ALL if present
      const withoutAll = current.filter(f => f !== 'ALL');
      const idx = withoutAll.indexOf(filter);
      if (idx > -1) {
        withoutAll.splice(idx, 1);
      } else {
        withoutAll.push(filter);
      }
      this.selectedFilters.next(withoutAll.length > 0 ? withoutAll : ['ALL']);
    }
  }

  onSearchInputChange(event: Event): void {
    const query = (event.target as HTMLInputElement).value;
    this.searchQuery.next(query);
  }

  processAllReadyOrders(): void {
    // Implement logic to process all ready orders
    console.log('Processing all ready orders');
  }

  exportOrders(): void {
    // Implement logic to export orders
    console.log('Exporting orders');
  }

  showDetails(ecommerceOrder: EcommerceOrder): void {
    this.selectedEcommerceOrder = ecommerceOrder;
    // Belt and braces. The details component emits `false` from every cancel
    // and save path, but it cannot emit anything once it has been destroyed --
    // see the note in goBackToOrders(). Clearing on the way IN means a flag
    // left stale by any route at all cannot follow the operator to the next
    // order and disable its back button for no visible reason.
    this.editingOrder = false;
  }

  /**
   * Leave the details view, asking first if an edit is open.
   *
   * Previously the back button was simply `[disabled]="editingOrder"`. That is
   * the worst of the three options: the control is visibly there, the operator
   * clicks it, nothing happens, and nothing on screen says why -- they are left
   * to work out for themselves that some section below is mid-edit and has to
   * be saved or cancelled first. A dead control teaches nothing. Asking the
   * question states the problem and offers both answers.
   *
   * Discarding is done by dropping `selectedEcommerceOrder`, which destroys the
   * details component and takes its in-progress form state with it. We do NOT
   * reach in and call its per-section cancel methods: there are four
   * independent editors in there (quantity, product, customer, carrier
   * service), a fifth would be easy to add and easy to forget here, and
   * destruction covers all of them for free. The edits live in `FormControl` /
   * `FormGroup` state rather than on the EcommerceOrder, so nothing is written
   * back by walking away.
   *
   * `editingOrder` is cleared explicitly rather than waited on. Once the `*ngIf`
   * drops the child, its `@Output` can never fire again, so the `true` it last
   * emitted would otherwise stick -- and the next order opened would come up
   * with its back button already disabled.
   */
  goBackToOrders(): void {
    if ( !this.editingOrder ) {
      this.selectedEcommerceOrder = null;
      return;
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        // ONE lookup, not `'Go Back' + '?'`. Gluing the question mark on in
        // code looks harmless and is not: Spanish opens a question with an
        // inverted mark and Greek writes it as a semicolon, so the punctuation
        // has to travel with the sentence rather than be concatenated by the
        // caller. '?' was also never in the LanguagePipe registry, so it only
        // ever resolved by falling back to its own literal and warning.
        title: LanguagePipe.get('Go Back', 'compressSpaces'),
        // The id is the English sentence, so an un-synced bundle still renders
        // a readable sentence rather than a developer token.
        body: LanguagePipe.get('You are still editing this order. Going back now will discard the changes you have not saved.', 'compressSpaces'),
        noButton: LanguagePipe.get('Cancel', 'compressSpaces'),
        yesButton: LanguagePipe.get('Go Back', 'compressSpaces'),
        returnValue: 'ok'
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      // Anything other than 'ok' -- including the backdrop click and Escape,
      // which both close with undefined -- means stay and keep editing. That is
      // the safe default for a dialog whose confirm branch destroys work.
      if ( result === 'ok' ) {
        this.editingOrder = false;
        this.selectedEcommerceOrder = null;
      }
    });
  }

  /**
   * True when at least one line on this order carries a sku.
   *
   * The Items cell is a dense little table inside an already-crowded row, so
   * the SKU column is only rendered for orders that actually have one. Not
   * every gateway sends a sku, and an always-present empty column would cost
   * width on every row to show nothing.
   */
  hasItemSku(row: any): boolean {
    const items = row && row.bookorder ? row.bookorder.items : null;
    if ( !items || items.length === 0 ) {
      return false;
    }
    for ( const item of items ) {
      if ( item && item['sku'] && String(item['sku']).trim().length > 0 ) {
        return true;
      }
    }
    return false;
  }

  // ---------------------------------------------------------------------------
  // Multi-column sorting
  //
  // The sort stack is ordered most-recently-chosen first: sortStack[0] is the
  // primary sort, sortStack[1] the secondary tie-breaker, and so on.
  // ---------------------------------------------------------------------------

  isSortable(column: string): boolean {
    return this.unsortableColumns.indexOf(column) === -1;
  }

  /** Cycles a column asc -> desc -> off and promotes it to primary. */
  toggleSort(column: string): void {
    if (!this.isSortable(column)) {
      return;
    }
    const existingIndex = this.sortStack.findIndex(criterion => criterion.column === column);
    if (existingIndex === -1) {
      this.sortStack.unshift({column: column, direction: 'asc'});
    } else {
      const existing = this.sortStack[existingIndex];
      this.sortStack.splice(existingIndex, 1);
      if (existing.direction === 'asc') {
        this.sortStack.unshift({column: column, direction: 'desc'});
      }
      // A 'desc' column is simply dropped from the stack (third click clears it).
    }
    this.resort();
  }

  /** 'asc' | 'desc' when the column takes part in the sort, otherwise ''. */
  getSortDirection(column: string): string {
    const criterion = this.sortStack.find(c => c.column === column);
    return criterion ? criterion.direction : '';
  }

  /** 1-based position in the sort stack, or 0 when the column is not sorted. */
  getSortPriority(column: string): number {
    return this.sortStack.findIndex(c => c.column === column) + 1;
  }

  clearSort(): void {
    this.sortStack = [];
    this.resort();
  }

  private resort(): void {
    this.dataSource = new MatTableDataSource(this.applySort(this.lastFilteredOrders));
  }

  private applySort(orders: EcommerceOrder[]): EcommerceOrder[] {
    if (!orders || orders.length === 0 || this.sortStack.length === 0) {
      return orders ? orders.slice() : [];
    }
    const stack = this.sortStack.slice();
    return orders.slice().sort((a, b) => {
      for (const criterion of stack) {
        const comparison = this.compareValues(this.getSortValue(a, criterion.column), this.getSortValue(b, criterion.column));
        if (comparison !== 0) {
          return criterion.direction === 'desc' ? -comparison : comparison;
        }
      }
      return 0;
    });
  }

  private compareValues(a: any, b: any): number {
    const aMissing = (a === null || a === undefined || a === '');
    const bMissing = (b === null || b === undefined || b === '');
    if (aMissing && bMissing) {
      return 0;
    }
    if (aMissing) {
      return 1;  // Empty values always sort to the end
    }
    if (bMissing) {
      return -1;
    }
    if (typeof a === 'number' && typeof b === 'number') {
      return a === b ? 0 : (a < b ? -1 : 1);
    }
    return ('' + a).localeCompare('' + b, undefined, {numeric: true, sensitivity: 'base'});
  }

  /** The value a column is ordered by. Mirrors what the column displays. */
  private getSortValue(order: EcommerceOrder, column: string): any {
    if (!order) {
      return '';
    }
    switch (column) {
      case 'ecommerceConnection':
        return (order.ecommerceConnection ? order.ecommerceConnection.type + ' ' + order.ecommerceConnection.ID : '');
      case 'order':
        return (order.bookorder && order.bookorder.simpleOrderId) ? '' + order.bookorder.simpleOrderId : (order.remoteOrderId || '');
      case 'date':
        return Util.getDateTimestamp(order.createdDate);
      case 'status':
        // Group by the statuses that drive the row colouring, most actionable first.
        return [order.ignoreStatus, order.functionalStatus, order.userHoldStatus, order.releaseStatus, order.costVsRevenueStatus].join('|');
      case 'customer':
        return ((order.lastname || '') + ' ' + (order.firstname || '') + ' ' + (order.company || '')).trim();
      case 'items':
        return order.getItems() ? order.getItems().length : 0;
      case 'revenueAndCost':
        return (order.productRevenue || 0) + (order.shippingRevenue || 0) + (order.taxRevenue || 0);
      case 'tracking':
        return (order.shippingInfo || '') + ' ' + (order.trackingNumbers || '') + ' ' + (order.shipmentUpdateStatus || '');
      default:
        return '';
    }
  }

  updateDisplayedColumns(): void {    if (this.selectedEcommerceConnection || this.ecommerceConnections.length <= 1) {
      this.displayedColumns = ['order', 'date', 'status', 'customer', 'items', 'revenueAndCost', 'tracking', 'details', 'actions'];
    } else {
      this.displayedColumns = ['ecommerceConnection', 'order', 'date', 'status', 'customer', 'items', 'revenueAndCost', 'tracking', 'details', 'actions'];
    }
  }

  /**
   * Tracking URL for one number.
   *
   * This returned `https://www.example.com/track?id=...` until 2026-09-13 -- a
   * placeholder with a "replace with actual logic" comment that shipped, so
   * every tracking number on this grid was a link to a domain reserved for
   * documentation. The real carrier logic already existed in
   * `FacilityPackageComponent`; both now call the same helper.
   *
   * No carrier is passed because `EcommerceOrder` does not carry one -- it has
   * only the comma-joined `trackingNumbers` string. The helper falls back to
   * the number's own prefix, which is the more reliable signal anyway.
   */
  getTrackingLink(trackingNumber: string): string {
    return getTrackingUrl(trackingNumber);
  }

  /**
   * The tracking numbers for a row, as a list.
   *
   * The template used to call `row.trackingNumbers.split(',')` directly, which
   * throws on the null the server sends for any order that has not shipped --
   * and an exception in a template takes the whole row's rendering with it.
   */
  getTrackingNumbers(ecommerceOrder: EcommerceOrder): string[] {
    return ecommerceOrder ? splitTrackingNumbers(ecommerceOrder.trackingNumbers) : [];
  }

  /**
   * True when there are more tracking numbers than the three we show.
   *
   * The template previously tested `row.trackingNumbers.length > 3`, which is
   * the length of the STRING, not the count -- so the "..." appeared on every
   * order with a single tracking number, all of which are far longer than three
   * characters.
   */
  hasMoreTrackingNumbers(ecommerceOrder: EcommerceOrder): boolean {
    return this.getTrackingNumbers(ecommerceOrder).length > 3;
  }

  // ---------------------------------------------------------------------------
  // Shipment state and the shipment-update push back to the store
  //
  // An EcommerceOrder is fulfilled by one or more PrintNodeOrders, joined on
  // `order_ID`. "Shipped" is therefore a property of the WHOLE set: an order
  // with two PrintNodeOrders where only one has shipped is NOT shipped, and the
  // customer must not be told that it is.
  // ---------------------------------------------------------------------------

  /**
   * The PrintNodeOrders that fulfil this EcommerceOrder.
   *
   * Two links, in order of trust:
   *
   * 1. `ecommerceOrder.printNodeOrderIds` -- the explicit set from the server.
   *    When present it is authoritative about *how many* there are, which lets
   *    the caller tell "we hold all of them" from "we hold some of them"; see
   *    `hasCompletePrintNodeOrderSet()`. Requested in
   *    docs/contracts/outbox/2026-09-12-ecommerce-order-printnodeorderids.md.
   * 2. `order_ID` -- the legacy join. The server confirmed (2026-09-11 reply,
   *    section 3) that this column does NOT exist on its EcommerceOrder entity
   *    and is never sent; the client constructor only ever sets it to 0. Kept
   *    for locally-populated objects only.
   *
   * Until (1) arrives, an empty result means UNKNOWN, never "none" -- the
   * PrintNodeOrders may simply be outside the window the startup load pulls.
   * Going via Order is not an option: `OrderService` keeps a private, non-live
   * cache that it clears and refills per query, so it cannot answer this off
   * the client.
   */
  getPrintNodeOrdersForEcommerceOrder(ecommerceOrder: EcommerceOrder): PrintNodeOrder[] {
    if ( !ecommerceOrder ) {
      return [];
    }
    // Preferred link: the explicit id list from the server. Asked for in
    // docs/contracts/outbox/2026-09-12-ecommerce-order-printnodeorderids.md.
    const ids = ecommerceOrder.printNodeOrderIds;
    if ( ids && ids.length > 0 ) {
      return this.printNodeOrderService.objects.filter(printNodeOrder => printNodeOrder && ids.indexOf(printNodeOrder.ID) >= 0);
    }
    if ( ids && ids.length === 0 ) {
      // The server said there are none. That is an answer, not a gap -- see
      // getShippedState(), which reads it as NOT_SHIPPED rather than UNKNOWN.
      return [];
    }
    // Legacy fallback. order_ID is 0 on every row the server sends (it
    // confirmed the column does not exist on its entity), so this finds nothing
    // in practice and is kept only for any locally-populated object.
    if ( !ecommerceOrder.order_ID ) {
      return [];
    }
    return this.printNodeOrderService.objects.filter(printNodeOrder => printNodeOrder && printNodeOrder.order_ID === ecommerceOrder.order_ID);
  }

  /**
   * The ids of the PrintNodeOrders fulfilling this order, as one display string
   * for the Order column -- '' when there is nothing definite to show.
   *
   * '' deliberately folds together the two cases that have no id to print:
   * `undefined` (an old server that never sends the array) and `[]` (a current
   * server saying no fulfilment exists yet). The cell would render the same
   * blank for both either way, and the distinction that actually matters --
   * "cannot see them" vs "there are none" -- is already carried by the shipped
   * indicators, which is where it changes what the user can do.
   *
   * Read off `printNodeOrderIds` and NOT off
   * `getPrintNodeOrdersForEcommerceOrder()`. The id list is the complete set
   * from the server; the PrintNodeOrder cache is only a recent window, so going
   * via the objects would quietly drop the ids of fulfilments outside that
   * window and present a short list as though it were the whole story.
   *
   * Sorted so the cell does not reorder itself between responses.
   */
  getPrintNodeOrderIdsLabel(ecommerceOrder: EcommerceOrder): string {
    const ids = ecommerceOrder ? ecommerceOrder.printNodeOrderIds : undefined;
    if ( !ids || ids.length === 0 ) {
      return '';
    }
    return ids.slice().sort((a, b) => a - b).join(', ');
  }

  /**
   * True when the server told us which PrintNodeOrders fulfil this order AND we
   * hold every one of them locally.
   *
   * The count check is the point. The PrintNodeOrder cache is a recent window,
   * so an order can easily have 3 fulfilments of which we hold 2 -- and those 2
   * can both be SHIPPED while the third is still in production. Without this,
   * that order would report SHIPPED and offer the resend button.
   */
  private hasCompletePrintNodeOrderSet(ecommerceOrder: EcommerceOrder): boolean {
    const ids = ecommerceOrder ? ecommerceOrder.printNodeOrderIds : undefined;
    if ( !ids ) {
      return false;
    }
    return this.getPrintNodeOrdersForEcommerceOrder(ecommerceOrder).length === ids.length;
  }

  /** A PrintNodeOrder counts as shipped on either of the two status fields the server maintains. */
  private isPrintNodeOrderShipped(printNodeOrder: PrintNodeOrder): boolean {
    if ( !printNodeOrder ) {
      return false;
    }
    return printNodeOrder.printNodeOrderStatus === 'SHIPPED' || printNodeOrder.shippingStatus === 'SHIPPED';
  }

  /**
   * A cancelled fulfilment must not hold an order back from counting as shipped.
   *
   * The server sends the COMPLETE set in `printNodeOrderIds`, unfiltered by
   * status, and asked us to do this filtering ourselves -- see
   * docs/contracts/inbox/_processed/2026-09-12-printnodeorderids-complete-set-restored-and-creation-republishes.md.
   * It briefly excluded CANCELLED server-side and reverted, for two good
   * reasons: the excluded rows were still needed to resolve status, and
   * membership of the array would otherwise depend on a mutable field with no
   * event to notify on.
   *
   * `shippingStatus` is the single signal. The `hold` column is deliberately
   * NOT consulted even though it carries `CANCEL_REQUESTED`, `CANCELED` and
   * `PARTIALLY_CANCELED` -- a cancellation *request* is not a cancellation, and
   * the server keys off `shippingStatus` alone.
   */
  private isPrintNodeOrderCancelled(printNodeOrder: PrintNodeOrder): boolean {
    return !!printNodeOrder && printNodeOrder.shippingStatus === 'CANCELLED';
  }

  /**
   * Three-state, because two states would be a lie.
   *
   *   'SHIPPED'     every live fulfilment is known to have shipped
   *   'NOT_SHIPPED' at least one live fulfilment is known NOT to have shipped
   *   'UNKNOWN'     we cannot see the fulfilments at all
   *
   * The distinction is the whole point. A boolean would have to fold UNKNOWN
   * into one of the other two, and both choices are wrong: folding into SHIPPED
   * offers the resend button on orders still in production; folding into
   * NOT_SHIPPED hides the button whenever the PrintNodeOrders are outside the
   * cached window.
   *
   * `printNodeOrderIds` is authoritative when present. The server sends it on
   * every response and never sends `null` or omits it, so an absent array means
   * an old server, not a gap -- that is the only case left that can reach the
   * legacy branch.
   *
   * NOTE the roll-up fallback (`productionStatus`, `shippedTimestamp`) was
   * DELETED here on 2026-09-12 at the server's request: those columns are not
   * on its entity and are never sent, so the branch implied a data source that
   * does not exist.
   */
  getShippedState(ecommerceOrder: EcommerceOrder): string {
    if ( !ecommerceOrder ) {
      return 'UNKNOWN';
    }
    const ids = ecommerceOrder.printNodeOrderIds;
    if ( ids ) {
      // Authoritative and complete. Every branch below is definite.
      if ( ids.length === 0 ) {
        // No fulfilments at all. Nothing has shipped, and the server refuses a
        // resend on exactly this case with a 409.
        return 'NOT_SHIPPED';
      }
      if ( !this.hasCompletePrintNodeOrderSet(ecommerceOrder) ) {
        // We know how many there are and we are missing some, so we cannot
        // claim SHIPPED however the cached ones look.
        return 'UNKNOWN';
      }
      // Completeness is checked against the UNFILTERED match, above, because
      // the count has to line up with what the server sent. Cancellation is
      // filtered only here, where the question is what still has to ship.
      const live = this.getPrintNodeOrdersForEcommerceOrder(ecommerceOrder)
        .filter(printNodeOrder => !this.isPrintNodeOrderCancelled(printNodeOrder));
      if ( live.length === 0 ) {
        // Every fulfilment was cancelled. Nothing shipped, and nothing is going
        // to -- NOT_SHIPPED rather than SHIPPED, which would push a completion
        // for goods that never left.
        return 'NOT_SHIPPED';
      }
      for ( const printNodeOrder of live ) {
        if ( !this.isPrintNodeOrderShipped(printNodeOrder) ) {
          return 'NOT_SHIPPED';
        }
      }
      return 'SHIPPED';
    }
    // Legacy branch: an old server that does not send the array at all.
    const printNodeOrders = this.getPrintNodeOrdersForEcommerceOrder(ecommerceOrder)
      .filter(printNodeOrder => !this.isPrintNodeOrderCancelled(printNodeOrder));
    if ( printNodeOrders.length > 0 ) {
      for ( const printNodeOrder of printNodeOrders ) {
        if ( !this.isPrintNodeOrderShipped(printNodeOrder) ) {
          return 'NOT_SHIPPED';
        }
      }
      return 'SHIPPED';
    }
    return 'UNKNOWN';
  }

  /** True only when EVERY PrintNodeOrder fulfilling this order is known to have shipped. */
  allPrintNodeOrdersShipped(ecommerceOrder: EcommerceOrder): boolean {
    return this.getShippedState(ecommerceOrder) === 'SHIPPED';
  }

  /** Whether the row should be drawn as shipped. */
  isShipped(ecommerceOrder: EcommerceOrder): boolean {
    return this.getShippedState(ecommerceOrder) === 'SHIPPED';
  }

  /**
   * Membership of the 'Shipped' toolbar filter.
   *
   * Deliberately NOT the same predicate as `isShipped()`, because the two
   * questions differ on the UNKNOWN case. `isShipped()` drives a colour and an
   * action button, where UNKNOWN must fail closed -- offering a resend for an
   * order still in production pushes a completion for goods that never left.
   * A filter only decides what a user is shown, so failing closed there means
   * an order that HAS shipped silently disappears from the one list meant to
   * contain it, and nothing on screen says why.
   *
   * So the server roll-up is consulted, but only where we have no better
   * answer:
   *
   *   SHIPPED      definite, from `printNodeOrderIds` -- include
   *   NOT_SHIPPED  equally definite -- exclude, and the roll-up does NOT get
   *                to override it. The roll-up has NO BACKFILL (the server
   *                declined one on purpose: for an old order the packages may
   *                be purged, so it would write SHIPPED over a partial set),
   *                which means every historical order reads 'NONE' whether it
   *                shipped or not. A field that cannot distinguish "did not
   *                ship" from "was never computed" must only ever be able to
   *                ADD rows to this filter, never remove them.
   *   UNKNOWN      the fulfilments are outside the cached window, so
   *                `shippingStatus` is the only signal there is.
   *
   * `shippingStatus` is populated as of the server change of 2026-09-16 (see
   * inbox/_processed/2026-09-16-standardized-publishes-image-versions-reported-and-shipping-rollups-built.md
   * section 8) and carries the same enum as `PrintNodeOrder.shippingStatus`:
   * NONE | SHIPPED | ERROR | CANCELLED. Only 'SHIPPED' qualifies -- note that
   * 'NONE' covers "only SOME fulfilments have shipped", which is precisely the
   * partly-shipped order this filter must not claim.
   */
  matchesShippedFilter(ecommerceOrder: EcommerceOrder): boolean {
    if ( !ecommerceOrder ) {
      return false;
    }
    const shippedState = this.getShippedState(ecommerceOrder);
    if ( shippedState === 'SHIPPED' ) {
      return true;
    }
    if ( shippedState === 'UNKNOWN' ) {
      return ecommerceOrder.shippingStatus === 'SHIPPED';
    }
    return false;
  }

  /** 'INITIAL' is the do-nothing default and is not worth the width in a dense row. */
  showShipmentUpdateStatus(ecommerceOrder: EcommerceOrder): boolean {
    return !!(ecommerceOrder && ecommerceOrder.shipmentUpdateStatus && ecommerceOrder.shipmentUpdateStatus !== 'INITIAL');
  }

  /**
   * Colour for the shipment-update status text.
   *
   * Only 'ERROR' earns a colour. 'WAITING' means queued-and-not-yet-attempted,
   * NOT failed -- the server was explicit about that -- so it must not be drawn
   * as a problem.
   */
  getShipmentUpdateStatusColor(ecommerceOrder: EcommerceOrder): string {
    if ( ecommerceOrder && ecommerceOrder.shipmentUpdateStatus === 'ERROR' ) {
      return 'darkred';
    }
    return '';
  }

  /**
   * True once the completion + tracking push is known to have reached the
   * remote store.
   *
   * 'SENT' is the ONLY value that means done. The other three all mean the
   * store has not been told yet, for different reasons:
   *
   *   INITIAL  nothing has been pushed
   *   WAITING  queued for a later sweep -- queued is not sent
   *   ERROR    attempted and failed
   *
   * So this tests for 'SENT' explicitly rather than excluding 'ERROR' and
   * 'INITIAL': a value we have not seen (a new member of the server's enum)
   * then reads as "not sent", which errs towards drawing attention rather
   * than towards silently hiding a push that never happened.
   */
  isShipmentUpdateSent(ecommerceOrder: EcommerceOrder): boolean {
    return !!ecommerceOrder && ecommerceOrder.shipmentUpdateStatus === 'SENT';
  }

  /**
   * Shipped, and the remote store has not been told yet.
   *
   * This earns the row the LOUD shipped colour (`mediumpurple`). A shipped
   * order whose completion has already been pushed is finished and gets the
   * quiet one (`plum`) instead -- it is still worth seeing that the order has
   * left the building, it is just not worth shouting about. Colouring every
   * shipped order the same way made the highlight a synonym for "shipped",
   * which is most of the grid most of the time, and a colour that is nearly
   * always on carries no information.
   *
   * Kept strict (`isShipped()`, i.e. `printNodeOrderIds` only, UNKNOWN reads as
   * no) on purpose, even though the quiet colour uses the looser
   * `matchesShippedFilter()`. The loud colour says "there is something to do
   * here", and the button that does it -- `canResendShipmentUpdate()` -- is
   * equally strict. Loosening this one alone would highlight rows that offer no
   * action, which is worse than not highlighting them at all.
   *
   * NOT the same predicate as `canResendShipmentUpdate()`, which deliberately
   * stays available on already-sent orders: a store can lose or reject a
   * completion it acknowledged, so re-pushing must remain possible. Offering
   * the action and demanding attention are different questions.
   */
  needsShipmentUpdate(ecommerceOrder: EcommerceOrder): boolean {
    return this.isShipped(ecommerceOrder) && !this.isShipmentUpdateSent(ecommerceOrder);
  }

  /**
   * The resend button is only offered once the whole order has physically
   * shipped. Resending earlier would push a completion message, and tracking
   * numbers that do not exist yet, for goods still in production.
   *
   * It stays offered after a successful push -- the row simply stops being
   * highlighted; see `needsShipmentUpdate()`.
   */
  canResendShipmentUpdate(ecommerceOrder: EcommerceOrder): boolean {
    if ( !ecommerceOrder || ecommerceOrder.ignoreStatus === 'IGNORE' ) {
      return false;
    }
    // 'NOT_SHIPPED' and 'UNKNOWN' both withhold the button. The server enforces
    // the same rule and answers 409 with per-PrintNodeOrder reasons, so if our
    // view is stale and the button is offered wrongly, the push is still
    // refused and the user is told why -- see resendShipmentUpdate().
    return this.getShippedState(ecommerceOrder) === 'SHIPPED';
  }

  resendShipmentUpdate(ecommerceOrder: EcommerceOrder, element: any): void {
    // Re-checked here and not only in the template: the template guard can go
    // stale between render and click if a notification arrives in between.
    if ( !this.canResendShipmentUpdate(ecommerceOrder) ) {
      return;
    }
    element.classList.add('spinner');
    this.ecommerceOrderService.resendShipmentUpdate(ecommerceOrder).subscribe(
      response => {
        element.classList.remove('spinner');
        // The server re-reads the row AFTER the push, so the response already
        // carries the new shipmentUpdateStatus / trackingNumbers /
        // remoteOrderState. Applying it directly means the row settles
        // immediately instead of waiting on the notification round trip. The
        // notification is published too, and is idempotent against this.
        if ( response && response.ecommerceOrder ) {
          ecommerceOrder.update(response.ecommerceOrder);
        }
        // warnings[] can be NON-EMPTY on a SUCCESS -- e.g. "no tracking numbers
        // were found, the completion was sent without them" -- so it is
        // surfaced on the happy path too, not only on failure.
        const warnings = ( response && response.warnings ) ? response.warnings : [];
        if ( response && response.status === 'SUCCESS' ) {
          this.showResendResult(warnings.length > 0 ? warnings.join(' ') : 'Shipment update sent.');
        } else {
          this.showResendResult(warnings.length > 0 ? warnings.join(' ') : 'Shipment update failed.');
        }
      },
      error => {
        element.classList.remove('spinner');
        // 409 carries the per-PrintNodeOrder reasons the order is not shippable.
        // This is the case the client guard is meant to prevent, so reaching it
        // means our cached view disagreed with the server -- show the reason
        // rather than a generic failure.
        const warnings = ( error && error.error && error.error.warnings ) ? error.error.warnings : [];
        console.error('resendShipmentUpdate failed', error);
        this.showResendResult(warnings.length > 0 ? warnings.join(' ') : 'Shipment update failed.');
      }
    );
  }

  private showResendResult(message: string): void {
    this.snackBar.open(message, null, {duration: 6000});
  }

  // getStatusColor(ecommerceOrder: EcommerceOrder): string {
    // switch (ecommerceOrder.) {
    //   case 'SHIPPED':
    //   case 'DELIVERD':
    //     return 'darkgreen';
    //   case 'ADDRESS ISSUE':
    //   case 'SHIPPING_ISSUE':
    //   case 'PRODUCT_ISSUE':
    //     return 'red';
    //   case 'IN_PRODUCTION':
    //     return 'darkgoldenrod';
    //   case 'MANUAL_HOLD':
    //   case 'ON_HOLD':
    //     return 'darkorange';
    //   default:
    //     return '';
    // }
  // }

  /**
   * The row background is the only at-a-glance signal on this grid, so two
   * states must never share a colour -- or a near-shade of one.
   *
   * The palette, in the order the branches are evaluated. Keep this list
   * accurate; it is the only place the whole scheme is visible at once, and the
   * two bugs fixed on 2026-09-12 were both invisible when reading the branches
   * one at a time.
   *
   *   darkgrey      ignored
   *   pink          functional error
   *   cyan          ready to release, but held
   *   lightcyan     held
   *   lightblue     cost-vs-revenue review
   *   mediumpurple  SHIPPED, but the store has not been told yet -- act
   *   plum          SHIPPED and the store knows -- finished, nothing to do
   *   lightgreen    READY to release -- the one green, and it means "act"
   *   lightgrey     released (SUCCESS) and NOT yet shipped -- in production
   *   yellow        INITIAL
   *   lightyellow   release IN_PROGRESS
   *   navajowhite   NOT_READY / REVIEW_SOURCE
   *   orange        ERROR, and the default
   *
   * Three rules that fall out of it, all learned the hard way:
   *
   * 1. **Only one green.** Green reads as "this one is ready for you". Shipped
   *    orders need no action, so colouring them green-ish inverted the meaning
   *    of the strongest signal on the screen.
   * 2. **Only CSS named colours.** An invented name (`lightorange`) is silently
   *    dropped by the browser, and a state that fails to paint is worse than a
   *    state painted the wrong colour, because nothing looks broken.
   * 3. **Two states of one concept share a hue and differ in strength; two
   *    different concepts never share a hue.** `cyan` / `lightcyan` is the
   *    precedent -- both mean "held", and the saturated one is the one that
   *    also wants releasing. `mediumpurple` / `plum` is the same pairing for
   *    "shipped". Differing only in LIGHTNESS is what failed (see below), so
   *    the pair has to separate on saturation as well.
   */
  getRowBackgroundColor(ecommerceOrder: EcommerceOrder): string {
      //   if ( filters.includes('NEED_ATTENTION')) {
      //     if ( order.functionalStatus !== 'SUCCESS') {
      //       keep = true;
      //     }
      //     if ( order.releaseStatus === 'ERROR_REMOTE_ORDERID') {
      //       keep = true;
      //     }
      //   }
      //   if ( filters.includes('READY_TO_RELEASE')) {
      //     if ( order.releaseStatus === 'READY') {
      //       keep = true;
      //     }
      //   }
      //   if ( filters.includes('PROCESSING')) {
      //     if ( order.functionalStatus === 'SUCCESS' && order.releaseStatus === 'NOT_READY' ) {
      //       keep = true;
      //     }
      //   }
      //   if ( filters.includes('RELEASED')) {
      //     if ( order.releaseStatus === 'SUCCESS') {
      //       keep = true;
      //     }
      //   }
      //   if ( filters.includes('ON_HOLD')) {
      //     if ( order.userHoldStatus === 'HOLD') {
      //       keep = true;
      //     }
      //   }
      //   return keep;
      // });
    // if ( ecommerceOrder.uniqueRemoteOrderIdStatus === 'ERROR' ) {
    //   return 'red';
    // }
    if ( ecommerceOrder.ignoreStatus === 'IGNORE') {
      return 'darkgrey';
    }
    if ( ecommerceOrder.functionalStatus !== 'SUCCESS' && ecommerceOrder.functionalStatus !== 'INITIAL') {
      return 'pink';
    }
    if ( ecommerceOrder.releaseStatus === 'READY' && ecommerceOrder.userHoldStatus === 'HOLD') {
      return 'cyan';
    }
    if ( ecommerceOrder.userHoldStatus === 'HOLD') {
      return 'lightcyan';
    }
    if ( ecommerceOrder.ignoreStatus === 'IGNORE') {
      return 'darkgrey';
    }
    if ( ecommerceOrder.costVsRevenueStatus === 'REVIEW' ) {
      return 'lightblue';
    }
    // Shipped is checked after the exception states above (ignored, functional
    // error, hold, cost review) so a shipped order that still needs a human
    // keeps its attention colour. It is checked BEFORE the releaseStatus switch
    // because every shipped order is also released, and 'SUCCESS' -> lightgrey
    // would otherwise swallow it -- which is exactly what it did until
    // 2026-09-17: "released, in production" and "shipped, delivered to the
    // customer" were the same grey, so the end of the pipeline was invisible.
    //
    // TWO shipped colours, because there are two states and only one of them
    // wants anything from the user:
    //
    //   mediumpurple  shipped, and the remote store has NOT been told yet.
    //                 These are the rows where the resend button is the point.
    //   plum          shipped, and the push landed. Finished. Quiet on purpose.
    //
    // Between 2026-09-16 and 2026-09-17 only the first of those was coloured
    // and the second fell through to lightgrey. That was deliberate -- before
    // 2026-09-16 EVERY shipped order was coloured, which was most of the grid
    // most of the time, and a colour that is nearly always on says nothing.
    // But collapsing it all the way back onto `lightgrey` overshot: it is worth
    // seeing that an order has left the building, it is just not worth SHOUTING
    // it. A quiet colour of its own is the answer; the loud one stays reserved.
    //
    // The two predicates are deliberately different strengths:
    //
    //   needsShipmentUpdate()   strict -- `printNodeOrderIds` only. The loud
    //                           colour must only appear where the resend button
    //                           is actually offered (canResendShipmentUpdate()
    //                           is equally strict), because "act on this" with
    //                           no available action is worse than no highlight.
    //   matchesShippedFilter()  looser -- falls back to the server roll-up when
    //                           the fulfilments are outside the cached window,
    //                           so a row that the Shipped FILTER admits is also
    //                           COLOURED as shipped. A filter and a colour that
    //                           disagree teach the user to trust neither.
    //
    // NOT a green. It was `palegreen` until 2026-09-12, which collided with the
    // `lightgreen` used two lines below for "READY to release": the two differ
    // by about 8% lightness and read as the same colour across a dense grid, so
    // orders that had already gone to production looked like orders waiting to
    // be sent there. That is the worst possible confusion on this screen --
    // green here means "act on it", and these rows are the ones needing no
    // action at all.
    //
    // The purple pair separates on hue (260 vs 300), saturation and lightness
    // at once, which is the mistake the palegreen/lightgreen clash teaches:
    // a single-axis difference is not a difference at a glance. Nothing else in
    // the palette is purple, so neither can be confused with a non-shipped row.
    if ( this.needsShipmentUpdate(ecommerceOrder) ) {
      return 'mediumpurple';
    }
    if ( this.matchesShippedFilter(ecommerceOrder) ) {
      return 'plum';
    }
    switch (ecommerceOrder.releaseStatus ) {
      case 'READY': {
        // TODO: Add case for 'RELEASE_PENDING_BALANCE'
        return 'lightgreen';
      }
      case 'SUCCESS': {
        return 'lightgrey';
      }
      case 'INITIAL': {
        return 'yellow';
      }
      case 'ERROR': {
        return 'orange';
      }
      case 'IN_PROGRESS': {
        return 'lightyellow';
      }
      // `lightorange` is NOT a CSS named colour. Both of these returned it
      // until 2026-09-12, so the browser discarded the declaration and the rows
      // rendered with no background at all -- indistinguishable from a row the
      // function never coloured. Same class of bug as the green collision
      // above: a state that the palette silently failed to convey.
      // `navajowhite` is the real pale orange.
      case 'NOT_READY': {
        return 'navajowhite';
      }
      case 'REVIEW_SOURCE': {
        return 'navajowhite';
      }
      default: {
        return 'orange';
      }
    }
  }

  private generateWeekOptions(): WeekOption[] {
    const options: WeekOption[] = [];
    const today = new Date();
    for (let i = 0; i < 5; i++) { // Generate options for the current and previous 4 weeks
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - (today.getDay() - 1) - (7 * i)); // Monday of the week
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      options.push({
        value: new Date(startOfWeek),
        viewValue: `${startOfWeek.toLocaleDateString()} - ${endOfWeek.toLocaleDateString()}`
      });
    }
    return options;
  }

  private getCurrentWeekStart(): Date {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 for Sunday, 1 for Monday, etc.
    const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Adjust to Monday
    return new Date(today.setDate(diff));
  }

  private updateSummaryData(orders: EcommerceOrder[]): void {
    this.ordersCount$ = new BehaviorSubject(orders.length);
    this.orderItemsCount$ = new BehaviorSubject(orders.reduce((sum, order) => sum + order.getItems().length, 0));
    this.ordersShippedCount$ = new BehaviorSubject(orders.filter(order => order.releaseStatus === 'SUCCESS').length);
    this.ordersHoldCount$ = new BehaviorSubject(orders.filter(order => order.userHoldStatus === 'HOLD').length);
    this.ordersCostReviewCount$ = new BehaviorSubject(orders.filter(order => order.costVsRevenueStatus === 'REVIEW').length);
    this.ordersNeedReviewCount$ = new BehaviorSubject(orders.filter(order => order.releaseStatus !== 'SUCCESS' && ( order.addressStatus !== 'SUCCESS' || order.productStatus !== 'SUCCESS' || order.carrierServiceLevelStatus !== 'SUCCESS' || order.productStatus !== 'SUCCESS') ).length);
    this.totalProductionCostShippedDelivered$ = new BehaviorSubject(orders.filter(order => order.releaseStatus !== 'SUCCESS' && order.functionalStatus === 'SUCCESS').reduce((sum, order) => sum + order.productionCost + order.shippingCost + order.taxCost, 0));
  }

  compute(ecommerceOrder: EcommerceOrder, element: any): void {
    element.classList.add('spinner');
    this.ecommerceOrderService.compute(ecommerceOrder).subscribe(e => {
      element.classList.remove('spinner');
      console.log('compute');
    });
  }

  /**
   * Toggle the operator-level "take this out of my queue" flag.
   *
   * `ignoreStatus` is queue state, NOT order state. It decides only whether the row appears in the
   * working filters (`NEED_ATTENTION`, `READY_TO_RELEASE`, `PROCESSING`, `RELEASED`, `SHIPPED`,
   * `ON_HOLD` and `ALL`) or drops into `IGNORED`. It releases nothing and it cancels nothing.
   *
   * It is therefore offered at *any* `releaseStatus`, including `'SUCCESS'`. The template used to
   * hide the toggle behind `releaseStatus !== 'SUCCESS'`, which left two dead ends: a released order
   * that still had to be cleared from the queue (duplicate, cancelled at the store, handled by hand)
   * could not be ignored, and an order ignored before release could never be un-ignored afterwards --
   * it was stuck in the `IGNORED` filter, the one place it was still visible.
   *
   * Stopping work that was already released is a separate action -- the PrintNodeOrders have to be
   * cancelled on their own. This only changes what the operator is shown.
   *
   * Set-then-update is deliberate: the server's `compute()` preserves `ignoreStatus` on the row it
   * returns (docs/contracts/outbox/_sent/2026-08-27-ack-compute-preserves-ignorestatus.md), so the
   * next refresh does not undo a manual ignore.
   */
  setIgnoretatus(ecommerceOrder: EcommerceOrder, status: string, element: any): void {
    element.classList.add('spinner');
    ecommerceOrder.ignoreStatus = status;
    this.ecommerceOrderService.updateItem(ecommerceOrder).subscribe(e => {
      element.classList.remove('spinner');
      console.log('set ignoreStatus');
    });
  }
  setUserHoldStatus(ecommerceOrder: EcommerceOrder, status: string, element: any): void {
    element.classList.add('spinner');
    ecommerceOrder.userHoldStatus = status;
    this.ecommerceOrderService.updateItem(ecommerceOrder).subscribe(e => {
      element.classList.remove('spinner');
      console.log('set userHoldStatus');
    });
  }

  protected release(ecommerceOrder: EcommerceOrder, element: any): void {
    element.classList.add('spinner');
    ecommerceOrder.userHoldStatus = status;
    ecommerceOrder.releaseStatus = 'RELEASED';
    this.ecommerceOrderService.release(ecommerceOrder).subscribe(e => {
      element.classList.remove('spinner');
      console.log('release');
    });
  }

  readRemoteOrders($event: any) {
    if ( this.selectedEcommerceConnection && this.selectedEcommerceConnection.type !== 'CSV' ) { // TODO: have the ecommerceConnecton identify if orders can be read
      const element: any = event.target;
      element.classList.add('spinner');
      this.ecommerceConnectionService.readOrders(this.selectedEcommerceConnection).subscribe(e => {
        element.classList.remove('spinner');
        console.log('published product');
      });
    }
  }
}
